/* eslint-disable function-paren-newline */
import * as React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { generatePath } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import tableColumnsFormatter from '../utils/tableColumnsFormatter';
import { TradeType } from '../api/trades';
import Table from '../organisms/Table';
import ActionsPanel from '../organisms/ActionsPanel';
import useTablePage from '../hooks/useTablePage';
import { convertTableBoolean } from '../utils/convertTableValues';
import {
  ModalDefaultDraggable,
  LoaderWithContainer,
} from '../molecules';
import {
  getProjects,
  createProject,
  updateProject,
} from '../api/projects';
import { getPermits, createPermit } from '../api/permits';
import ModalInfo from '../molecules/ModalInfo';
import ProjectFormContainer from '../forms/ProjectFormContainer';
import { ROUTES } from '../constants/routes';
import { getVocabulariesDataList } from '../api/vocabulary';
import ProjectEditForm from '../forms/ProjectEditForm';
import { getJurisdictions } from '../api/jurisdiction';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import { getUsers, getUsersByProject, getUsersCustom } from '../api/users';
import { USER_TYPES_VALUES } from '../constants/forms';
import DashboardBlocks from './DashBoardBlocks';
import useDidUpdateEffect from '../hooks/useDidUpdate';
import ProjectsTable from './ProjectsPage/ProjectsTable';
import { RootState } from '../redux/root';
import { setNewProjectModalOpen, setProjectCandidate, setProjectImport } from '../redux/slices/projectSlice';
import { apiSlice, useSubmitProjectImportMutation } from '../redux/rtkQuery/apiSlice';
import MondayLeadsNotice from '../organisms/MondayLeadsNotice';
import { buildProjectImportFormValues, ProjectImportLead } from '../utils/projectImportFormValues';

const DASHBOARD_SEARCH_KEY = 'dashboard.search';

const getStatuses = (permits, statuses) =>
  permits.map(
    permit =>
      statuses.find(status => permit.status === status.id)
        ?.title,
  );

const modifiedGetProjectsWithPermits = params =>
  Promise.all([
    getProjects({ ...params, ordering: null }),
    getVocabulariesDataList({
      ...GET_ALL_QUERY_PARAMS,
      vocabularyEntity: 6,
      ordering: 'title',
    }), // statuses
  ]).then(
    ([
      projectsResponse,
      statusesResponse,
    ]) => {
      return {
        ...projectsResponse,
        data: {
          ...projectsResponse.data,
          results: projectsResponse.data.results.map(
            project => {
              const projectPermits = project?.permits;

              return {
                ...project,
                permitsNames: projectPermits.map(
                  permit => permit.name,
                ),
                permits: projectPermits,
                permitsNumbers: projectPermits.map(permit =>
                  permit.number ? permit.number : '',
                ),
                permitsStatuses: getStatuses(
                  projectPermits,
                  statusesResponse.data.results,
                ),
                address: `${project.street}, ${project.city}, ${project.state}, ${project.zipCode}`,
              };
            },
          ),
        },
      };
    },
  );

const ProjectsPage = () => {
  const [
    isExistingProjectChosen,
    setIsExistingProjectChosen,
  ] = React.useState(false);

  const [
    addContactModal,
    setAddContactModal,
  ] = React.useState(false);

  const [modalTitle, setModalTitle] = React.useState(
    'Add Project',
  );
 const newProjectModalOpen = useSelector((s:RootState) => s.projectsSlice.newProjectModalOpen);
  const [
    isEditProjectModal,
    setEditProjectModal,
  ] = React.useState(false);

  const [projectData, setProjectData] = React.useState({});

  const [
    isEditProjectMode,
    setEditProjectMode,
  ] = React.useState(false);

  const [isLoading, setIsLoading] = React.useState(false);
  const [showDashboardBlocks, setShowDashboardBlocks] = React.useState(() => {
    const stored = localStorage.getItem('projectsPage.showDashboardBlocks');
    return stored === null ? true : stored === 'true';
  });
  const [
    jurisdictionsData,
    setJurisdictionsData,
  ] = React.useState([]);
  const [
    projectManagersData,
    setProjectManagersData,
  ] = React.useState([]);
  const [contactData, setContactsData] = React.useState([]);
  const [defaultValues, setDefaultValues] = React.useState(
    {},
  );
  const HandleAddContactModal = flag => {
    // window.scrollTo({ top: 0, behavior: 'smooth', });
    setAddContactModal(flag);
  };
  const setInitialProjectValues = (
    initial,
    jurisdiction,
    manager,
    contacts,
  ) => {
    if (Object.keys(projectData).length) {
      setDefaultValues({
        ...initial,
        jurisdiction: jurisdiction.filter(
          el => el.id === initial.jurisdiction,
        )[0],
        manager: manager.find(
          el => el.id === initial?.manager,
        ),
        contacts: contacts.filter(el =>
          initial.contacts.includes(el.id) ||
          (initial.companyContacts || []).includes(el.id),
        ),
      });
    }
  };

  useDidUpdateEffect(() => {
    if (Object.keys(projectData).length > 0) {
      setIsLoading(true);
      getUsersByProject(projectData?.id)
        .then(individualClientsResponse => {
          const projectContacts = individualClientsResponse.data.filter(
            el => el?.isActive,
          );
          setContactsData(projectContacts);
          setDefaultValues({
            ...projectData,
            contacts: projectContacts.filter(el =>
              projectData.contacts?.includes(el.id),
            ),
          });
        })
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
  }, [projectData]);

  const loadEditData = () => {
    setIsLoading(true);
    Promise.all([
      getJurisdictions({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'name',
      }),
      getUsers({
        ...GET_ALL_QUERY_PARAMS,
        userRole: USER_TYPES_VALUES.EMPLOYEE,
      }),
    ])
      .then(([jurisdictionsResponse, projectManagersResponse]) => {
        setJurisdictionsData(jurisdictionsResponse.data.results);
        setProjectManagersData(projectManagersResponse.data.results);
        setDefaultValues(prev => ({
          ...prev,
          jurisdiction: jurisdictionsResponse.data.results.find(
            el => el.id === projectData.jurisdiction,
          ),
          manager: projectManagersResponse.data.results.find(
            el => el.id === projectData?.manager,
          ),
        }));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
        setEditProjectMode(true);
      });
  };

  React.useEffect(() => {
    if (isExistingProjectChosen) {
      setModalTitle('Add Permit');
    }
  }, [isExistingProjectChosen]);

  const history = useHistory();
  const dispatch = useDispatch();
  const projectImport = useSelector((s: RootState) => s.projectsSlice.projectImport);
  const [submitProjectImport] = useSubmitProjectImportMutation();

  // When the wizard was opened from a Monday lead, link the saved project to
  // the lead so it leaves the notice. A failed link must not fail the save
  // (the project already exists); the lead just stays in the list.
  const linkMondayLead = endpoint => async fields => {
    const res = await endpoint(fields);
    if (projectImport) {
      const projectId = isExistingProjectChosen ? fields.project : res?.data?.id;
      if (projectId) {
        try {
          await submitProjectImport({ id: projectImport.id, project: projectId }).unwrap();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Could not link Monday lead to project', error);
        }
      }
      dispatch(setProjectImport(null));
    }
    return res;
  };

  const {
    search,
    setSearch,
    modalError,
    modalSuccess,
    isModalOpen,
    // modalTitle,
    modalErrorTitle,
    tableManuallyUpdate,
    createOrUpdate,
    handleAdd,
    handleCloseModal,
    handleCloseModalInfo,
    updateTable,
  } = useTablePage<TradeType>({
    createEndpoint: linkMondayLead(isExistingProjectChosen
      ? createPermit
      : createProject),
    updateEndpoint: updateProject,
    modalCreateTitle: modalTitle,
  });

  // Restore the dashboard's own search on mount so it survives
  // navigating away (other pages share state.filters.searchToken) and reloads.
  useEffect(() => {
    const saved = localStorage.getItem(DASHBOARD_SEARCH_KEY) || '';
    setSearch(saved);
  }, []);

  useEffect(() => {
    if (search) {
      localStorage.setItem(DASHBOARD_SEARCH_KEY, search);
    } else {
      localStorage.removeItem(DASHBOARD_SEARCH_KEY);
    }
  }, [search]);

  useEffect(() => {
    dispatch(setNewProjectModalOpen(isModalOpen));
  }, [isModalOpen]);
  const columnsOrder = [
    'name',
    'permitsNames',
    'permitsNumbers',
    'permitsStatuses',
    'address',
    'jurisdictionName',
    'franchiseName',
  ];

  const columns = column => {
    if (
      [
        'id',
        'isDeleted',
        'franchise',
        'jurisdiction',
        'street',
        'city',
        'state',
        'zipCode',
        'serviceType',
        'contacts',
        'manager',
        'createdAt',
        'franchiseName',
        'permits',
        'numPermits',
      ].includes(column)
    ) {
      return null;
    }

    switch (column) {
      case 'name':
        return 'Project Name';
      case 'jurisdictionName':
        return 'Jurisdiction';
      case 'managerName':
        return 'Project Manager';
      case 'permitsNames':
        return 'Permit Name';
      case 'permitsNumbers':
        return 'Permit Number';
      case 'permitsStatuses':
        return 'Permit Status';
      default:
        return tableColumnsFormatter(column);
    }
  };

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
    dispatch(setNewProjectModalOpen(false));
    dispatch(setProjectCandidate(null));
    dispatch(setProjectImport(null));
  };

  const handleReviewLead = async (lead: ProjectImportLead) => {
    const formValues = await buildProjectImportFormValues(lead);
    dispatch(setProjectCandidate(null));
    dispatch(setProjectImport({ id: lead.id, formValues, requester: lead.requester }));
    handleAdd();
  };

  const handleCloseEditProject = () => {
    setProjectData({});
    setDefaultValues({});
    setEditProjectMode(false);
    setEditProjectModal(false);
  };

  const handlePermitClick = (data, column) => {
    if (column === 'name') {
      if (Object.keys(data).length) {
        setProjectData(data);
        setEditProjectModal(true);
      }
    } else {
      if (data.permitItemId) {
        const path = generatePath(ROUTES.PERMIT, {
          projectId: data.projectId,
          permitId: data.permitItemId,
        });
        history.push(path);
      }
      if (data.permits?.length === 1) {
        const path = generatePath(ROUTES.PERMIT, {
          projectId: data.id,
          permitId: data.permits[0].id,
        });
        history.push(path);
      }
    }
  };

  return (
    <>

      <Container>
        <DashboardToggleRow>
          <DashboardToggleButton
            type="button"
            onClick={() => {
              const next = !showDashboardBlocks;
              setShowDashboardBlocks(next);
              localStorage.setItem('projectsPage.showDashboardBlocks', String(next));
            }}
            aria-expanded={showDashboardBlocks}
          >
            <Chevron $open={showDashboardBlocks}>▾</Chevron>
            {showDashboardBlocks ? 'Hide dashboard' : 'Show dashboard'}
          </DashboardToggleButton>
        </DashboardToggleRow>
        {showDashboardBlocks && <DashboardBlocks />}
        <MondayLeadsNotice onReview={handleReviewLead} />
        <ActionsPanel
          onSearch={setSearch}
          onAdd={handleAdd}
          isBreadCrumbsVisible={false}
          isActionPanelMoreVisible={false}
          buttonTitle="Add Project"
        />
        <ProjectsTable handleCellClick={(data, column) =>
            handlePermitClick(data, column)
          }
        />
      </Container>

      {isEditProjectModal && (
        <ModalDefaultDraggable
          title={
            isEditProjectMode
              ? 'Edit Project'
              : 'Project Details'
          }
          addContactModal={addContactModal}
          onClose={handleCloseEditProject}
        >
          {isLoading && (
              <LoaderWithContainer />
            )}
          {!isLoading &&
            !!Object.keys(defaultValues).length && (
              <ProjectEditForm
                HandleAddContactModal={HandleAddContactModal}
                editMode={isEditProjectMode}
                initialValues={defaultValues}
                changeMode={() => loadEditData()}
                jurisdiction={jurisdictionsData}
                manager={projectManagersData}
                contacts={contactData}
                handleCloseEditProject={
                  handleCloseEditProject
                }
                updateTable={updateTable}
              />
            )}
        </ModalDefaultDraggable>
      )}

      {(isModalOpen || newProjectModalOpen) && (
        <ModalDefaultDraggable
          onClose={handleCloseForm}
          title={modalTitle}
          wide
        >
          <ProjectFormContainer
            handleCloseMainForm={handleCloseForm}
            isExistingProjectChosen={
              isExistingProjectChosen
            }
            setIsExistingProjectChosen={
              setIsExistingProjectChosen
            }
            isModalError={modalErrorTitle.length > 0}
            onSubmit={async data => {
              const res = await createOrUpdate(data);
              await dispatch(
                apiSlice.endpoints.getProjects.initiate(
                  { limit: 25, offset: 0 },
                  { forceRefetch: true, subscribe: false },
                ),
              );
              dispatch(apiSlice.util.invalidateTags(['Projects']));
              return res;
            }}
            setModalTitle={setModalTitle}
            jurisdictionsData={jurisdictionsData}
          />
        </ModalDefaultDraggable>
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo
          timeToClose={5000}
          isError={modalError}
          onClose={handleCloseModalInfo}
          errorMsg={modalErrorTitle}
        />
      )}
    </>
  );
};

const Container = styled.div`
  margin: 20px 0 0 0;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 840px) {
    margin: 14px 0 0 0;
  }

  @media (max-width: 540px) {
    margin: 10px 0 0 0;
  }
`;

const DashboardToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const DashboardToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  color: var(--neutral-500);
`;

export default ProjectsPage;
